import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClearRefinements, useCurrentRefinements, useInstantSearchContext } from 'react-instantsearch';

import { type BookmakersConfig } from '@cx-modules/types';

import { Accordion, Button } from '~/apps/product-plus/components/cxy/cxy.ts';
import { type BetInsightsConfig } from '~/apps/product-plus/configuration/BetInsightsConfiguration';
import {
  type BettingInsightsContent,
  type BettingInsightsResultAttribute,
  type EsIndex,
} from '~/apps/product-plus/configuration/types';
import { type BettingInsightsFilter } from '~/apps/product-plus/configuration/ui/BettingInsightsFilter';

import { trackMobileFiltersClearAllClick } from '../../tracking';

import styles from './styles/Filters.module.css';
import { SortDropdown } from './SortDropdown';

export function Filters<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>({
  bookmakers,
  canSort,
  components,
  config,
  indexName,
  isDisabled = false,
}: {
  bookmakers: BookmakersConfig;
  canSort: boolean;
  components: BettingInsightsFilter<TIndex>[];
  config: BetInsightsConfig<TIndex, TContent>;
  indexName: TIndex;
  isDisabled?: boolean;
  resourcesUrl: string;
}) {
  const { t } = useTranslation();
  const { helper } = useInstantSearchContext();
  const { canRefine, refine } = useClearRefinements({
    excludedAttributes: config.ui.refinements.clearAll.excludedAttributes,
  });
  const { items: currentRefinements } = useCurrentRefinements();

  const sortingFn = config?.sortingFn;

  const sorting = useMemo(() => {
    return sortingFn({
      currentRefinements: currentRefinements.map(({ attribute, ...rest }) => ({
        attribute: attribute as BettingInsightsResultAttribute<TIndex>,
        ...rest,
      })),
    });
  }, [sortingFn, currentRefinements]);

  const lightRepubs = new Set(['GZ', 'WS']);
  const repub = config.ui.refinements.repubConfig.name;
  const chevronStroke = lightRepubs.has(repub) ? 'light' : 'bold';

  return (
    <>
      {canSort && (
        <div className={styles.section}>
          <SortDropdown indexName={indexName} isDisabled={config.isWalled} sorting={sorting} />
        </div>
      )}
      <div className={styles.section}>
        <div className={styles.sectionWrapper}>
          <h2 id="filters-heading">{t('CX_PLUS_FILTERS')}</h2>
          <Button
            className={styles.clear}
            disabled={isDisabled || !canRefine}
            onClick={() => {
              trackMobileFiltersClearAllClick();
              helper?.clearRefinements();
              refine();
            }}
            variant="ghost"
          >
            {t('CX_PLUS_CLEAR_ALL')}
          </Button>
        </div>
        <Accordion chevronStroke={chevronStroke} isDense name="Filters" type="multiple">
          {components.map(refinement => {
            const { attribute, getLabel } = refinement;
            const label = getLabel();
            const component = refinement.render({
              bookmakers,
              currentRefinements,
            });
            if (!component) {
              return null;
            }

            return (
              <Accordion.Item
                classNames={{ content: styles.accordionContent }}
                heading={label}
                isOpen={attribute !== 'bookmakerCode'}
                key={`refinement-${attribute}-${label}`}
              >
                {component}
              </Accordion.Item>
            );
          })}
        </Accordion>
      </div>
    </>
  );
}
